import React, { useContext, useState } from 'react';
import { UnarchiveCategoryModalView, UnarchiveCategoryModalViewProps } from './UnarchiveCategoryModal.view';
import { ApolloError, gql, useApolloClient } from '@apollo/client';
import {
  Category,
  CategoryArchiveFilter,
  GetAutoImportsDocument,
  GetTimeslotsDocument,
  PlanUsageType,
  UpdateCategoryInput,
  UpdateCategoryMutationFn,
  UsageFieldsFragmentDoc
} from '../../../../models/graphql';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { showToast } from '../../../../helpers/showToast';
import { useSelectedOrganization } from '../../../../hooks/useSelectedOrganization';
import { AppContext } from 'providers/AppProvider';
import { PossibleMultipleLimits } from 'modules/PlanLimit/LimitReachedModal/LimitReachedModal.view';

export type UnarchiveCategoryModalControllerProps = Omit<UnarchiveCategoryModalViewProps, 'onSubmit' | 'submitted'> & {
  updateCategory: UpdateCategoryMutationFn;
};

export const UnarchiveCategoryModalController = (props: UnarchiveCategoryModalControllerProps): JSX.Element => {
  const apolloClient = useApolloClient();
  const { cache } = apolloClient;
  const [submitted, setSubmitted] = useState<boolean>(false);
  const { selectedWorkspace } = useSelectedOrganization();
  const { setLimitReachedModalState, setPollUsage } = useContext(AppContext);
  const workspaceId = selectedWorkspace?.id ?? '';

  const onSubmit = () => {
    setSubmitted(true);

    const input: UpdateCategoryInput = {
      archived: false
    };

    props
      .updateCategory({
        variables: { categoryId: props.categoryId, input: input },
        update: () => {
          props.onClose();
        }
      })
      // Filter this category from the archived cache.
      .then(() => {
        setPollUsage({ amountPoll: 2, interval: 3000, shouldPoll: true }); // to update the post usage
        cache.modify({
          id: cache.identify({ id: workspaceId, __typename: 'Workspace' }),
          fields: {
            [`categories:{"statusFilter":"${CategoryArchiveFilter.ARCHIVED}"}`](existing, { readField }) {
              return existing.filter((category: Category) => readField('id', category) !== props.categoryId);
            }
          }
        });
        // Add this category reference to the active cache.
        cache.modify({
          id: cache.identify({ id: workspaceId, __typename: 'Workspace' }),
          fields: {
            [`categories:{"statusFilter":"${CategoryArchiveFilter.ACTIVE}"}`](existing) {
              // This generates a reference we can use.
              const newRef = cache.writeFragment({
                data: { id: props.categoryId, __typename: 'Category' },
                fragment: gql`
                  fragment Category on Category {
                    id
                  }
                `
              });
              return [...existing, newRef];
            }
          }
        });
        const oldUsage: PlanUsageType | null = cache.readFragment({
          fragment: UsageFieldsFragmentDoc,
          fragmentName: 'UsageFields',
          id: 'PlanUsageType:{}'
        });
        const newUsage = { ...oldUsage, activeCategories: (oldUsage?.activeCategories ?? 0) + 1 };
        cache.writeFragment({
          fragment: UsageFieldsFragmentDoc,
          fragmentName: 'UsageFields',
          id: 'PlanUsageType:{}',
          data: newUsage
        });
        // When we fetch data, the backend API most likely filters out ones that are attached to archived categories.
        // We need to re-fetch this data when un-archiving.
        apolloClient.refetchQueries({
          include: [GetTimeslotsDocument, GetAutoImportsDocument]
        });
      })
      .catch(e => {
        const errorForRemapping = e as ApolloError;
        const mappedError = remapApolloErrors(errorForRemapping);
        if (getApolloErrorMessage(e) === 'PLAN_LIMIT_EXCEEDED') {
          const detailsErrorMessage = e.networkError.result.errors[0].extensions.message;
          const regexToFindQtyLimitsExceeded = /^\s*-\s+(\S+)/gm;
          const limitsExceeded: PossibleMultipleLimits[] = [
            ...detailsErrorMessage.matchAll(regexToFindQtyLimitsExceeded)
          ].map(match => match[1].toLowerCase());
          const uniqueLimits = Array.from(new Set(limitsExceeded));
          if (uniqueLimits.length >= 1) {
            const firstElement = uniqueLimits.shift();
            firstElement &&
              setLimitReachedModalState({
                isOpen: true,
                type: 'multiple',
                limits: [firstElement, ...uniqueLimits]
              });
          }
        } else {
          showToast({
            summary: 'Error in un-archiving the category',
            detail: mappedError,
            severity: 'error'
          });
        }

        setSubmitted(false);
      });
  };
  const viewProps: UnarchiveCategoryModalViewProps = { ...props, onSubmit, submitted };
  return <UnarchiveCategoryModalView {...viewProps} />;
};
